<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { GetWishListDetail, RemoveItemFromWishList } from '../services/WishListService.ts'
import type { IAoeWishListDetails } from '../types/WishListTypes'
import { FormatCurrency } from '../services/currencyFormatter'
import { ref, onMounted } from 'vue'
import AoeAddToCartButton from '../components/AoeAddToCartButton.vue'
import {
  GetOrderTypeId,
  GetProductRootUrl,
  GetWishlistUrl,
} from '../stores/SettingsStore'
import { GetFullProductImageDictionary } from '../services/SkuService.ts'
import { trashIcon } from '@progress/kendo-svg-icons'
import { Button as KButton } from '@progress/kendo-vue-buttons'

const wishListDetails = ref({} as IAoeWishListDetails)

const logger = useLogger()
setStoreLogger(logger)

const urlParams = new URLSearchParams(window.location.search)
const wishlistname = urlParams.get('wishlistname')
logger.debug('wishlistname=', wishlistname)

const orderTypeId = ref('')
const productRootUrl = ref('')
const wishlistUrl = ref<string>('')
const productCodes = ref<string[]>([])
const productImageUrls = ref<Record<string, string>>({})

onMounted(async () => {
  logger.debug('onMounted')
  productRootUrl.value = await GetProductRootUrl()
  orderTypeId.value = await GetOrderTypeId()
  wishlistUrl.value = await GetWishlistUrl()

  if (wishlistname) {
    GetWishListDetail(wishlistname, orderTypeId.value).then(async data => {
      wishListDetails.value = data
      logger.debug('wishListDetails=', wishListDetails.value)

      for (const wishList of data.items) {
        productCodes.value.push(wishList.productCode)
      }

      productImageUrls.value = await GetFullProductImageDictionary(productCodes.value)
    })
  }
})

function GetFullProductImageUrlFromStringDictionary(productCode: string | null) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )

  if (productCode != null)
  {
    const productImageUrl = productImageUrls.value[productCode]
    logger.debug('productImageUrl=', productImageUrl)
    if (productImageUrl) return productImageUrl
  }
  return '#'
}

function deleteItemFromWishListClickedHandler(skuCode: string) {
  logger.debug('deleteItemFromWishListClickedHandler', skuCode)
  if (wishlistname != null)
  {
    RemoveItemFromWishList(skuCode, wishlistname).then(() => {

      const updatedWishListItems = wishListDetails.value.items.filter((item) => {
        return item.skuCode !== skuCode
      })

      wishListDetails.value.items = []
      wishListDetails.value.items = updatedWishListItems

    })
  }
}

</script>

<style lang="scss">
.wishlistModal {
  position: fixed;
  z-index: $z-modal-backdrop;
  top: 50%;
  left: 50%;
  width: 300px;
  margin: auto;
}

.wishlistModal-content {
  background-color: white;
  padding: $card-padding;
  border: 1px solid #888;
  width: 100%;
  border-radius: $radius-sm;
}

.wishListImage-Lead,
.wishListImage-ThumbnailList {
  width: 230px;
  height: 230px;
}

.wishListImage-Lead,
.ListCardImages-thumbnail {
  background-color: #e0e0e0;
}

.wishListImage-ThumbnailList {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: 50% 50%;
  gap: $spacing-1;
}

.wishListName {
  @include typography-h4;
  @include font-weight-bold;
}

.wishListItems {
  @include typography-body;
}

.imgWish {
  margin: auto;
  width: 100%;
  height: 100%;
}

.wishListSection {
  width: 100%;
}

.anchor {
  display: block;
  width: 30px;
}

button.wishListSkuDelete {
  color: black;
  border-radius: 50%;
  height: 40px;
  width: 40px;
  position: absolute;
  top: $spacing-2-5;
  right: $spacing-2-5;
  z-index: $z-above;

  .k-svg-icon {
    right: 6.5px;
    bottom: 2px;
  }

  svg {
    transform: scale(1.5);
  }
}

</style>

<template>
  <div class="breadcrumb">
    <div><a :href="wishlistUrl">{{ t('wishList.WishListDetailHeader') }}</a></div>
    <div>&nbsp;/&nbsp;</div>
    <div>{{ wishlistname }}</div>
  </div>
  <div class="aoeWishListDetail">
    <div class="wishListDetailSection">
      <div class="row wishListSection">
        <div class="col-12 wishListName">
          <h2>{{ wishlistname }}</h2>
        </div>
      </div>
      <div class="row wishListSection">
        <div class="col-12 wishListItemCount">
          <h4 v-if="wishListDetails.items != undefined">
            {{ wishListDetails.items.length }} {{ t('wishList.WishListItems') }}
          </h4>
        </div>
      </div>
    </div>
    <div class="row wishListSkuContainer">
      <div
        class="col-md-4 col-6 mt-2"
        v-for="w in wishListDetails.items"
        :key="w.aoeWishlistSkuId"
      >
        <div class="col-12 imageDiv">
          <KButton
            ref="btnDelete"
            class="wishListSkuDelete anchor"
            :svg-icon="trashIcon"
            @click="deleteItemFromWishListClickedHandler(w.skuCode)"
            >
            <span class="sr-only">{{ t('wishList.RemoveFromWishlist') }}</span>
          </KButton>
          <a
            v-bind:href="
              productRootUrl + '/' + w.categoryCode + '/' + w.productCode
            "
            data-category-code="@product.ProductCode"
          >
            <img
            :src="
                GetFullProductImageUrlFromStringDictionary(
                  w.productCode
                )
              "
              :alt="t('cart.ProductImageAlt')"
              class="img-fluid prodImage"
            />
          </a>
        </div>
        <a
          v-bind:href="
            productRootUrl + '/' + w.categoryCode + '/' + w.productCode
          "
          data-category-code="@product.ProductCode"
        >
          <div class="col-12 text-center prodName p-2">{{ w.productName }}</div>
          <div class="col-12 text-center prodPrice p-2">
            {{ FormatCurrency(w.price, null) }}
          </div>
        </a>
        <AoeAddToCartButton
          :ordertypeid="orderTypeId"
          :skucode="w.skuCode"
          :availablequantity="w.inventoryLevel.availableQty.toString()"
        ></AoeAddToCartButton>
      </div>
    </div>
  </div>
</template>
